import React, { useEffect, useState, useMemo } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import Select from '../../ui/selects/Select';
import { ShieldCheck, Trash2, Plus, Star, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';
import { useConfirm } from '../../ui/confirm/ConfirmContext';
import CustomModal from '../CustomModal';
import SecondaryButton from '../../ui/buttons/SecondaryButton';

const TYPE_OPTIONS = [
  { value: 'death', label: 'Cobertura por Morte' },
  { value: 'living', label: 'Cobertura em Vida' },
];

const emptyRow = () => ({
  id: null,
  name: '',
  coverageType: 'death',
  percent: '',
  amount: '',
  _new: true,
});

const ProtectionsModal = ({ clientId, protections = [], scope, onClose, onSuccess }) => {
  const { addToast } = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState({ protections: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [clientId, scope]);

  const fetchData = () => {
    setIsLoading(true);
    fetch(`/app/planner/client/${clientId}/dashboard/protections/protections?scope=${scope}`)
      .then((res) => res.json())
      .then((json) => {
        const camelData = camelizeKeys(json);
        setData(camelData);
      })
      .catch((err) => {
        console.error(err);
        addToast({ type: 'error', message: 'Erro ao carregar coberturas.' });
      })
      .finally(() => setIsLoading(false));
  };

  const updateRow = (index, field, value) => {
    setData((prev) => {
      const next = [...prev.protections];
      next[index] = { ...next[index], [field]: value };
      return { protections: next };
    });
  };

  const addRow = () => {
    setData((prev) => ({ protections: [...prev.protections, emptyRow()] }));
  };

  const removeRow = async (index) => {
    // index é da lista visível
    const row = visibleProtections[index];

    if (row?.id) {
      const ok = await confirm({
        title: 'EXCLUIR COBERTURA?',
        description: 'Esta ação removerá a cobertura definitivamente.',
        confirmText: 'EXCLUIR',
      });
      if (!ok) return;

      setData((prev) => {
        const all = prev.protections || [];
        // encontra o item correspondente na lista completa
        const realIdx = all.findIndex((r) => r === row || r.id === row.id);
        if (realIdx === -1) return prev;

        const next = [...all];
        next[realIdx] = { ...next[realIdx], _destroy: '1', __deleted: true }; // <- chave
        return { protections: next };
      });
    } else {
      // linha nova (sem id): remove de fato
      setData((prev) => {
        const all = prev.protections || [];
        // remove pelo índice na "visível"
        const toRemove = visibleProtections[index];
        const realIdx = all.findIndex((r) => r === toRemove);
        if (realIdx === -1) return prev;

        const next = [...all];
        next.splice(realIdx, 1);
        return { protections: next };
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSaving(true);

    const payload = snakecaseKeys({ protectionsAttributes: data.protections });

    console.log('payload', payload);

    fetchWithCsrf(`/app/planner/client/${clientId}/dashboard/protections/protections?scope=${scope}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Falha ao salvar');
        const json = await res.json();
        const camel = camelizeKeys(json);
        setData({ protections: camel.protections || [] });
        addToast({ type: 'success', message: 'Coberturas salvas com sucesso.' });
        onSuccess && onSuccess(camel);
        onClose && onClose();
      })
      .catch((err) => {
        console.error(err);
        addToast({ type: 'error', message: 'Não foi possível salvar. Tente novamente.' });
      })
      .finally(() => setIsSaving(false));
  };

  const visibleProtections = useMemo(() => (data.protections || []).filter((p) => !p.__deleted), [data.protections]);

  return (
    <CustomModal onClose={onClose}>
      <div className="w-full max-w-6xl mx-auto p-8 md:p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            Editar Coberturas
          </div>
          <button onClick={onClose} aria-label="Fechar">
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Títulos das colunas */}
        <div className="grid grid-cols-12 gap-4 text-blueDarker mb-2 text-lg">
          <div className="col-span-4">Nome</div>
          <div className="col-span-3">Tipo</div>
          {/* <div className="col-span-2">Percentual (%)</div> */}
          <div className="col-span-2">Valor (R$)</div>
        </div>

        {/* Lista */}
        <form onSubmit={handleSubmit}>
          <div className="max-h-custom500 overflow-y-auto flex flex-col gap-4">
            {isLoading && <div className="text-blueDark/70">Carregando coberturas…</div>}

            {visibleProtections.map((p, index) => (
              <div key={p.id ?? `tmp-${index}`} className="rounded-xl py-2 grid grid-cols-12 gap-4 items-center">
                {/* Nome */}
                <div className="col-span-6">
                  <Input
                    required
                    value={p.name}
                    onChange={(e) => updateRow(index, 'name', e.target.value)}
                    placeholder="Nome da cobertura"
                  />
                </div>

                {/* Tipo */}
                <div className="col-span-3">
                  <div className="relative">
                    <Select
                      required
                      value={p.coverageType}
                      onChange={(e) => updateRow(index, 'coverageType', e.target.value)}
                      options={TYPE_OPTIONS}
                    />
                  </div>
                </div>

                {/* Percentual (%)
                <div className="col-span-2">
                  <Input
                    required
                    value={p.percent}
                    onChange={(e) => updateRow(index, 'percent', e.target.value)}
                    placeholder="0"
                    inputMode="decimal"
                  />
                </div> */}

                {/* Valor (R$) */}
                <div className="col-span-2 sm:col-span-2">
                  <CurrencyInput
                    required
                    name={`amount-${index}`}
                    value={p.amount === '' || p.amount == null ? '' : Number(p.amount)}
                    onChange={(e) => {
                      const v = e?.target?.value;
                      updateRow(index, 'amount', v);
                    }}
                  />
                </div>

                {/* Remover */}
                <div className="col-span-1 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeRow(index)}
                    className="rounded-full p-2 hover:bg-red-50"
                    aria-label="Remover cobertura"
                    title="Remover cobertura"
                  >
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Adicionar nova cobertura e cancelar ou salvar */}
          <div className="mt-4 flex justify-between">
            <Button type="button" onClick={addRow} className="text-lg font-semibold">
              +
            </Button>
            <div className="flex items-center justify-end gap-4">
              <SecondaryButton type="button" onClick={onClose} disabled={isSaving}>
                CANCELAR
              </SecondaryButton>
              <Button type="submit" isLoading={isSaving}>
                SALVAR ALTERAÇÕES
              </Button>
            </div>
          </div>
        </form>
      </div>
    </CustomModal>
  );
};

export default ProtectionsModal;
